import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import AuthPage from './components/auth/AuthPage';
import NGODashboard from './components/ngo/NGODashboard';
import PublicDashboard from './components/public/PublicDashboard';
import SettingsPage from './components/common/SettingsPage';
import AboutPage from './components/common/AboutPage';
import Sidebar from './components/common/Sidebar';
import HistoryPage from './components/common/HistoryPage';
import RewardsPage from './components/common/RewardsPage';
import PartnersPage from './components/common/PartnersPage';
import WelcomeScreen from './components/common/WelcomeScreen';
import { AnimatePresence } from 'framer-motion';
import { API_URL } from './apiConfig';

const GOOGLE_MAPS_API_KEY = "AIzaSyCbJa4zUTRLOwvH9VLThomyxCh6_XXkG-g"; //.env was not working for some reason so had to do this eventhough its not safe, especially when my repo is public
const MAP_ID = "aff6da5783cf20155972f120";
const libraries = ['places', 'marker', 'visualization', 'routes'];

const Layout = ({ children, sidebarOpen, setSidebarOpen, userType }) => {
    const location = useLocation();
    const showMenuButton = !['/', '/welcome'].includes(location.pathname);

    return (
        <div className="relative min-h-screen">
            <Sidebar 
                isOpen={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                userType={userType}
            />
            {showMenuButton && (
                <button 
                    onClick={() => setSidebarOpen(true)} 
                    className="fixed top-4 left-4 z-30 bg-black bg-opacity-60 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-80 transition-all"
                    aria-label="Open menu"
                >
                    <span className="text-2xl font-light">&#9776;</span>
                </button>
            )}
            <main>
                <AnimatePresence mode="wait">
                    {children}
                </AnimatePresence>
            </main>
        </div>
    );
};

function App() {
    const [authenticated, setAuthenticated] = useState(false);
    const [userType, setUserType] = useState(null);
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    });
    
    useEffect(() => {
        if (loadError) {
            console.error("Google Maps API failed to load:", loadError);
        }
    }, [loadError]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const idToken = await firebaseUser.getIdToken(true);
                    // This call fetches the user's profile from DB.
                    // The backend's /auth route will handle creating the profile if it's the first time.
                    const response = await axios.post(
                        `${API_URL}/users/auth`,
                        { name: firebaseUser.displayName }, // Pass name for Google Sign-In
                        { headers: { Authorization: `Bearer ${idToken}` } }
                    );
                    
                    setUser(response.data);
                    setUserType(response.data.role);
                    setAuthenticated(true);
                } catch (error) {
                    console.error("Auth state sync error:", error);
                    // If backend says the user is invalid (e.g., wrong role), sign them out.
                    auth.signOut();
                    setAuthenticated(false);
                }
            } else {
                setAuthenticated(false);
                setUser(null);
                setUserType(null);
            }
            setAuthLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (authLoading) {
        return <div className="flex items-center justify-center h-screen bg-black text-white">Authenticating...</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
            <Router>
                <Layout
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    userType={userType}
                >
                    <Routes>
                        <Route path="/" element={
                            authenticated ? (
                                <Navigate to="/welcome" />
                            ) : (
                                <AuthPage />
                            )
                        } />
                        
                        <Route path="/welcome" element={
                            authenticated ? <WelcomeScreen user={user} userType={userType} /> : <Navigate to="/" />
                        } />

                        <Route path="/dashboard" element={authenticated ? <PublicDashboard user={user} setUser={setUser} isLoaded={isLoaded} loadError={loadError} MAP_ID={MAP_ID} /> : <Navigate to="/" />} />
                        <Route path="/history" element={authenticated ? <HistoryPage user={user} /> : <Navigate to="/" />} />
                        <Route path="/rewards" element={authenticated ? <RewardsPage user={user} /> : <Navigate to="/" />} />
                        <Route path="/partners" element={authenticated ? <PartnersPage user={user} setUser={setUser} /> : <Navigate to="/" />} />
                        <Route path="/settings" element={authenticated ? <SettingsPage user={user} setUser={setUser} /> : <Navigate to="/" />} />
                        <Route path="/about" element={authenticated ? <AboutPage /> : <Navigate to="/" />} />
                        <Route path="/ngo" element={authenticated && userType === 'ngo' ? <NGODashboard user={user} isLoaded={isLoaded} loadError={loadError} MAP_ID={MAP_ID} /> : <Navigate to="/" />} />
                        
                        <Route path="*" element={<Navigate to={authenticated ? (userType === 'ngo' ? '/ngo' : '/dashboard') : '/'} />} />
                    </Routes>
                </Layout>
            </Router>
        </div>
    );
}

export default App;
